import { call, put, select, StrictEffect, takeEvery } from "redux-saga/effects";

import { Paginated } from "@/jenesys-types/data/common";
import { DataType, FeatureType } from "@/jenesys-types/data/enums";
import { extractError } from "@/jenesys-types/errors";
import {
  IBankConnection,
  IChartOfAccounts,
  WhatsappConnectionStatus
} from "@/jenesys-types/jenesys-entities/config";

import { performDataAction, performFeatureAction } from "@/services/api";
import { selectActiveCompanyId } from "@/store/features/companies/companies";
import { toastError, toastSuccess } from "@/util/helpers";

import {
  authorizeYapily,
  AuthorizeYapilyAction,
  authorizeYapilyFailure,
  authorizeYapilySuccess,
  AuthorizeYapilySuccessAction,
  fetchBanks,
  FetchBanksAction,
  fetchBanksFailure,
  fetchBanksSuccess,
  loadAccountingData,
  LoadAccountingDataAction,
  loadAccountingDataFailure,
  loadAccountingDataSuccess,
  loadAccountingIntegrationFailure,
  LoadAccountingIntegrationsDataAction,
  loadAccountingIntegrationSuccess,
  loadAccountingShortData,
  LoadAccountingShortDataAction,
  loadAccountingShortDataFailure,
  loadAccountingShortDataSuccess,
  loadBankConnections,
  LoadBankConnectionsAction,
  loadBankConnectionsFailure,
  loadBankConnectionsSuccess,
  loadChartOfAccounts,
  LoadChartOfAccountsAction,
  loadChartOfAccountsFailure,
  loadChartOfAccountsSuccess,
  loadEmailStatus,
  LoadEmailStatusAction,
  loadEmailStatusFailure,
  loadEmailStatusSuccess,
  loadIntegrationAuthorize,
  loadIntegrationAuthorizeFailure,
  loadIntegrationAuthorizeSuccess,
  loadIntegrationDisconnect,
  loadIntegrationDisconnectFailure,
  loadIntegrationDisconnectSuccess,
  loadIntegrationRefresh,
  loadIntegrationRefreshFailure,
  loadIntegrationRefreshSuccess,
  loadLinkBusiness,
  LoadLinkBusinessAction,
  loadLinkBusinessFailure,
  loadLinkBusinessSuccess,
  LoadMailAuthAction,
  loadMailAuthorize,
  loadMailAuthorizeFailure,
  loadMailAuthorizeSuccess,
  LoadPlatformAuthAction,
  LoadPlatformDisconnectAction,
  LoadPlatformRefreshAction,
  loadPlatformStatus,
  LoadPlatformStatusAction,
  loadPlatformStatusFailure,
  loadPlatformStatusSuccess,
  loadWhatsappStatus,
  loadWhatsappStatusFailure,
  loadWhatsappStatusSuccess,
  removeBankConnection,
  removeBankConnectionFailure,
  removeBankConnectionSuccess,
  specifyConnectionDuration,
  SpecifyConnectionDurationAction,
  specifyConnectionDurationFailure,
  specifyConnectionDurationSuccess,
  SubmitWhatsappNumberAction,
  submitWpNumber,
  submitWpNumberFailure,
  submitWpNumberSuccess,
  updateWhatsappNumber,
  UpdateWhatsappNumberAction,
  updateWhatsappNumberFailure,
  updateWhatsappNumberSuccess
} from "./connections";

export function* loadPlatformStatusSaga(
  action: LoadPlatformStatusAction
): Generator<StrictEffect, void, any> {
  const { clientId } = action.payload;
  const companyId = yield select(selectActiveCompanyId);
  try {
    const platformStatusResponse: any = yield call(
      performDataAction,
      DataType.Integrations,
      "list",
      { companyId: clientId || companyId }
    );
    yield put(loadPlatformStatusSuccess(platformStatusResponse));
  } catch (error) {
    yield put(loadPlatformStatusFailure(extractError(error)));
  }
}

export function* loadAccountingDataSaga(
  action: LoadAccountingDataAction
): Generator<StrictEffect, void, any> {
  const { clientId } = action.payload;
  const companyId = yield select(selectActiveCompanyId);
  try {
    const accountingDataResponse: any = yield call(
      performFeatureAction,
      FeatureType.Bookkeeping,
      "loadAccountingData",
      {
        companyId: clientId || companyId
      }
    );
    yield put(loadAccountingDataSuccess(accountingDataResponse));
  } catch (error) {
    yield put(loadAccountingDataFailure(extractError(error)));
  }
}

export function* loadAccountingShortDataSaga(
  action: LoadAccountingShortDataAction
): Generator<StrictEffect, void, any> {
  const { clientId } = action.payload;
  const companyId = yield select(selectActiveCompanyId);
  try {
    const accountingShortDataResponse: any = yield call(
      performDataAction,
      DataType.AccountingShort,
      "list",
      {
        companyId: clientId || companyId
      }
    );

    yield put(loadAccountingShortDataSuccess(accountingShortDataResponse));
  } catch (error) {
    yield put(loadAccountingShortDataFailure(extractError(error)));
  }
}

export function* loadIntegrationAuthSaga(
  action: LoadPlatformAuthAction
): Generator<StrictEffect, void, any> {
  const { clientId, platform, onSuccess } = action.payload;
  const companyId = yield select(selectActiveCompanyId);
  try {
    const integrationAuthResponse: any = yield call(
      performDataAction,
      DataType.IntegrationAuth,
      "list",
      {
        companyId: clientId || companyId,
        platform
      },
      {
        referrer: window.location.href
      }
    );

    yield put(loadIntegrationAuthorizeSuccess());
    if (onSuccess) onSuccess(integrationAuthResponse);
  } catch (error) {
    yield put(loadIntegrationAuthorizeFailure(extractError(error)));
  }
}

export function* loadMailAuthSaga(
  action: LoadMailAuthAction
): Generator<StrictEffect, void, any> {
  const { clientId, platform, onSuccess } = action.payload;
  const companyId = yield select(selectActiveCompanyId);
  try {
    const mailAuthResponse: any = yield call(
      performDataAction,
      DataType.MailAuth,
      "list",
      {
        companyId: clientId || companyId,
        platform
      },
      {
        referrer: window.location.href
      }
    );

    yield put(loadMailAuthorizeSuccess());
    if (onSuccess) onSuccess(mailAuthResponse);
  } catch (error) {
    yield put(loadMailAuthorizeFailure(extractError(error)));
  }
}

export function* loadIntegrationRefreshSaga(
  action: LoadPlatformRefreshAction
): Generator<StrictEffect, void, any> {
  const { clientId, platform, onSuccess, onError } = action.payload;
  const companyId = yield select(selectActiveCompanyId);
  try {
    yield call(performDataAction, DataType.IntegrationRefresh, "list", {
      companyId: clientId || companyId,
      platform
    });

    yield put(loadIntegrationRefreshSuccess());
    if (onSuccess) onSuccess();
  } catch (error) {
    yield put(loadIntegrationRefreshFailure(extractError(error)));
    if (onError) onError();
  }
}

export function* loadIntegrationDisconnectSaga(
  action: LoadPlatformDisconnectAction
): Generator<StrictEffect, void, any> {
  const { clientId, platform, type } = action.payload;
  const companyId = yield select(selectActiveCompanyId);
  try {
    if (type === "accounting") {
      yield call(performDataAction, DataType.IntegrationDisconnect, "list", {
        companyId: clientId || companyId,
        platform
      });
    } else if (type === "whatsapp") {
      yield call(performDataAction, DataType.WhatsappDisconnect, "create", {
        companyId: clientId || companyId
      });
    } else if (type === "gmail" || type === "outlook") {
      yield call(performDataAction, DataType.MailDisconnect, "create", {
        companyId: clientId || companyId,
        platform
      });
    } else {
      yield put(
        loadIntegrationRefreshFailure(
          extractError(`Unsupported integration type: ${type}`)
        )
      );
      return;
    }

    yield put(loadIntegrationRefreshSuccess());
    yield put(loadIntegrationDisconnectSuccess());
    toastSuccess("Disconnected successfully!");
    window.location.reload();
  } catch (error) {
    yield put(loadIntegrationRefreshFailure(extractError(error)));
    yield put(loadIntegrationDisconnectFailure(extractError(error)));
    toastError("Failed to disconnect!");
  }
}

export function* loadLinkBusinessSaga(
  action: LoadLinkBusinessAction
): Generator<StrictEffect, void, any> {
  const { clientId, data, onSuccess } = action.payload;
  const companyId = yield select(selectActiveCompanyId);
  try {
    yield call(
      performDataAction,
      DataType.LinkBusiness,
      "link",
      {
        companyId: clientId || companyId,
        platform: data.platform
      },
      undefined,
      data
    );

    yield put(loadLinkBusinessSuccess());
    if (onSuccess) onSuccess();
  } catch (error) {
    yield put(loadLinkBusinessFailure(extractError(error)));
  }
}

export function* loadAccountingIntegrationSaga(
  action: LoadAccountingIntegrationsDataAction
): Generator<StrictEffect, void, any> {
  const { clientId, platform } = action.payload;
  const companyId = yield select(selectActiveCompanyId);
  try {
    const accountingIntegrationResponse: any = yield call(
      performDataAction,
      DataType.AccountingIntegration,
      "list",
      {
        companyId: clientId || companyId,
        platform: platform!.toUpperCase()
      }
    );

    yield put(loadAccountingIntegrationSuccess(accountingIntegrationResponse));
  } catch (error) {
    yield put(loadAccountingIntegrationFailure(extractError(error)));
  }
}

export function* loadChartOfAccountsSaga(
  action: LoadChartOfAccountsAction
): Generator<StrictEffect, void, any> {
  const { clientId, params } = action.payload;
  const companyId = yield select(selectActiveCompanyId);
  const limit = 20;
  try {
    const coaResponse: Paginated<IChartOfAccounts> = yield call(
      performDataAction,
      DataType.ChartOfAccounts,
      "list",
      {
        companyId: clientId ?? companyId,
        search: params.searchQuery,
        offset: params.page * limit,
        limit
      }
    );

    yield put(loadChartOfAccountsSuccess(coaResponse));
  } catch (error) {
    yield put(loadChartOfAccountsFailure(extractError(error)));
  }
}

export function* loadEmailStatusSaga(
  action: LoadEmailStatusAction
): Generator<StrictEffect, void, any> {
  const { clientId, platform } = action.payload;
  const companyId = yield select(selectActiveCompanyId);
  try {
    const emailResponse: any = yield call(
      performDataAction,
      DataType.EmailStatus,
      "list",
      {
        companyId: clientId || companyId,
        platform
      }
    );

    yield put(loadEmailStatusSuccess(emailResponse));
  } catch (error) {
    yield put(loadEmailStatusFailure(extractError(error)));
  }
}

export function* loadWhatsappStatusSaga(): Generator<StrictEffect, void, any> {
  try {
    const whatsappResponse: WhatsappConnectionStatus = yield call(
      performDataAction,
      DataType.WhatsappStatus,
      "list"
    );

    yield put(loadWhatsappStatusSuccess(whatsappResponse));
  } catch (error) {
    yield put(loadWhatsappStatusFailure(extractError(error)));
  }
}

export function* loadWhatsappNumberSaga(
  action: SubmitWhatsappNumberAction
): Generator<StrictEffect, void, any> {
  const { clientId, userId, number, onSuccess, onError } = action.payload;
  const companyId = yield select(selectActiveCompanyId);
  try {
    yield call(
      performDataAction,
      DataType.WhatsappNumber,
      "create",
      undefined,
      undefined,
      {
        number,
        user_id: userId,
        invited_from_company_id: companyId || clientId
      }
    );

    yield put(submitWpNumberSuccess());
    if (onSuccess) onSuccess();
  } catch (error) {
    yield put(submitWpNumberFailure(extractError(error)));
    if (onError) onError(extractError(error));
  }
}

export function* loadBankConnectionsSaga(
  action: LoadBankConnectionsAction
): Generator<StrictEffect, void, any> {
  const { clientId } = action.payload;
  const companyId = yield select(selectActiveCompanyId);
  try {
    const bankConnectionsResponse: Array<IBankConnection> = yield call(
      performDataAction,
      DataType.BankConnections,
      "list",
      {
        companyId: clientId || companyId
      }
    );

    yield put(loadBankConnectionsSuccess(bankConnectionsResponse));
  } catch (error) {
    yield put(loadBankConnectionsFailure(extractError(error)));
  }
}

export function* removeBankConnectionSaga(
  action: ReturnType<typeof removeBankConnection>
): Generator<StrictEffect, void, any> {
  const { clientId, connectionId, onSuccess } = action.payload;
  try {
    yield call(performDataAction, DataType.BankConnections, "delete", {
      companyId: clientId,
      id: connectionId
    });

    yield put(removeBankConnectionSuccess());
    if (onSuccess) onSuccess();
    // Refresh list
    yield put(loadBankConnections(clientId));
  } catch (error) {
    yield put(removeBankConnectionFailure(extractError(error)));
    toastError("Unable to remove bank");
  }
}

export function* specifyConnectionDurationSaga(
  action: SpecifyConnectionDurationAction
): Generator<StrictEffect, void, any> {
  const { fromDate, connectionId, toDate, companyId, onSuccess } =
    action.payload;
  try {
    const response: any = yield call(
      performDataAction,
      DataType.ConnectionDuration,
      "create",
      {
        companyId,
        connectionId
      },
      undefined,
      {
        from_date: fromDate,
        to_date: toDate
      }
    );

    yield put(specifyConnectionDurationSuccess());
    if (onSuccess) onSuccess(response);
  } catch (error) {
    yield put(specifyConnectionDurationFailure(extractError(error)));
  }
}

export function* fetchBanksSaga(
  action: FetchBanksAction
): Generator<StrictEffect, void, any> {
  try {
    const response: any = yield call(
      performDataAction,
      DataType.Banks,
      "list",
      {},
      { query: action.payload.query }
    );
    yield put(fetchBanksSuccess(response));
  } catch (error) {
    yield put(fetchBanksFailure(extractError(error)));
  }
}

export function* authorizeYapilySaga(
  action: AuthorizeYapilyAction
): Generator<StrictEffect, void, any> {
  try {
    const response: AuthorizeYapilySuccessAction["payload"] = yield call(
      performDataAction,
      DataType.YapilyAuth,
      "create",
      {
        companyId: action.payload.companyId
      },
      undefined,
      {
        institution_id: action.payload.institutionId
      }
    );
    yield put(authorizeYapilySuccess());
    action.payload.onSuccess(response.authorisation_url);
  } catch (error) {
    yield put(authorizeYapilyFailure(extractError(error)));
  }
}

export function* updateWhatsappNumberSaga(
  action: UpdateWhatsappNumberAction
): Generator<StrictEffect, void, any> {
  const { connectionId, number, onSuccess, onError } = action.payload;
  try {
    yield call(
      performDataAction,
      DataType.UpdateWhatsappNumber,
      "update",
      { id: connectionId },
      undefined,
      { number }
    );
    yield put(updateWhatsappNumberSuccess());
    if (onSuccess) onSuccess();
  } catch (error) {
    const extractedError = extractError(error);
    yield put(updateWhatsappNumberFailure(extractedError));
    if (onError) onError(extractedError);
  }
}

export default function* TeamSettingsSaga() {
  yield takeEvery(loadPlatformStatus.type, loadPlatformStatusSaga);
  yield takeEvery(loadAccountingData.type, loadAccountingDataSaga);
  yield takeEvery(loadAccountingShortData.type, loadAccountingShortDataSaga);
  yield takeEvery(loadIntegrationAuthorize.type, loadIntegrationAuthSaga);
  yield takeEvery(loadMailAuthorize.type, loadMailAuthSaga);
  yield takeEvery(loadIntegrationRefresh.type, loadIntegrationRefreshSaga);
  yield takeEvery(
    loadIntegrationDisconnect.type,
    loadIntegrationDisconnectSaga
  );
  yield takeEvery(loadLinkBusiness.type, loadLinkBusinessSaga);
  yield takeEvery(loadAccountingData.type, loadAccountingIntegrationSaga);
  yield takeEvery(loadChartOfAccounts.type, loadChartOfAccountsSaga);
  yield takeEvery(loadEmailStatus.type, loadEmailStatusSaga);
  yield takeEvery(loadWhatsappStatus.type, loadWhatsappStatusSaga);
  yield takeEvery(submitWpNumber.type, loadWhatsappNumberSaga);
  yield takeEvery(loadBankConnections.type, loadBankConnectionsSaga);
  yield takeEvery(
    specifyConnectionDuration.type,
    specifyConnectionDurationSaga
  );
  yield takeEvery(fetchBanks.type, fetchBanksSaga);
  yield takeEvery(authorizeYapily.type, authorizeYapilySaga);
  yield takeEvery(updateWhatsappNumber.type, updateWhatsappNumberSaga);
  yield takeEvery(removeBankConnection.type, removeBankConnectionSaga);
}
